import { FC, useState } from 'react'

import { ExternalLink, Icons, Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import useFormatDate from 'hooks/useFormatDate'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { Declaration as DeclarationType } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { AButton } from 'views/components/Analytics'
import { BorderedItem } from 'views/components/BorderedItem/BorderedItem'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal/styled'
import { AnalyticsPlaces } from 'views/pages/Deal/analyticsPlaces'
import { DeclarationModal } from 'views/pages/Deal/DealDetail/DealAccordion/DealFiles/DeclarationModal'
import { ButtonsWrapper, EntityTitle } from 'views/pages/Deal/DealDetail/DealAccordion/styled'

type Props = {
  declaration: DeclarationType | undefined
  declarationRefetch: refetchFunc
}

const Declaration: FC<Props> = ({ declaration, declarationRefetch }) => {
  const { t } = useTranslation(['deal', 'common'])
  const { id } = useParams()
  const notify = useNotification()
  const [isOpenModalAdd, setIsOpenModalAdd] = useState(false)
  const [isOpenConfirmRemove, setIsOpenConfirmRemove] = useState(false)

  const [removeProgress, removeHandler] = useAProgress(
    (dealId: string) => apiClient.delete(endpoints.dealDeclaration(dealId)),
    {
      eventName: 'removeDeclaration',
      onSuccess: async () => {
        await declarationRefetch()
        setIsOpenConfirmRemove(false)
        notify(Progress.SUCCESS, { title: t('declarationRemoveSuccess') })
      },
      onError: () => notify(Progress.ERROR, { title: t('common:something_went_wrong') }),
    },
  )
  const formatDate = useFormatDate()

  const addButton = (
    <AButton id="add" color="approve" Icon={Icons.IconAdd} onClick={() => setIsOpenModalAdd(true)}>
      {t('common:add')}
    </AButton>
  )

  return (
    <div>
      <EntityTitle>{t('declaration')}</EntityTitle>
      <div>
        {declaration ? (
          <BorderedItem>
            <ExternalLink href={declaration.files[0]?.uploaded_file} target="_blank">
              {t('declarationLinkName', {
                number: declaration.number,
                date: formatDate(declaration.start_date),
              })}
            </ExternalLink>

            <ButtonsWrapper>
              <AButton
                id="remove"
                color="secondary"
                background="none"
                Icon={Icons.IconDelete}
                onClick={() => setIsOpenConfirmRemove(true)}
              />
            </ButtonsWrapper>
          </BorderedItem>
        ) : (
          addButton
        )}
      </div>
      {isOpenModalAdd && <DeclarationModal onSuccess={declarationRefetch} onClose={() => setIsOpenModalAdd(false)} />}

      {isOpenConfirmRemove && (
        <ASharedModal
          id={AnalyticsPlaces.REMOVE_DECLARATION_MODAL}
          size="small"
          onClose={() => setIsOpenConfirmRemove(false)}
        >
          <SharedModalTitle>{t('declarationRemoveTitle')}</SharedModalTitle>
          <SharedModalContent>{t('declarationRemoveBody')}</SharedModalContent>
          <ADefaultModalFooter
            progress={removeProgress}
            onClose={() => setIsOpenConfirmRemove(false)}
            onConfirm={() => removeHandler(id)}
          />
        </ASharedModal>
      )}
    </div>
  )
}
export default Declaration
